/**
 * Custom helper that specifically checks if the passed-in value is an integer. Needed because
 * parseInt() returns a value as long as the beginning of the string is numbers. For example,
 * parseInt('123eif') returns 123. See here:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#description
 * @value The string value to validate.
 * @returns true if the passed-in value is an integer. false otherwise.
 */
export function isInteger(value: string) {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#a_stricter_parse_function
  return /^[-+]?(\d+|Infinity)$/.test(value)
}

/**
 * Returns a number between the passed in min/max values inclusively.
 * @param min The minimum number in the range.
 * @param max The maximum number in the range.
 * @returns A number within the min/max range (inclusively).
 */
export function randomMinMax(min: number, max: number): number {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random#getting_a_random_integer_between_two_values_inclusive
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export function numberOrNull(n: number | null | undefined): number | null {
  return n == null ? null : n
}

/**
 * If the passed in value is smaller than the max value, returns the number representation of it plus the offset.
 * Otherwise, returns null
 * @param The value to check (e.g. - "1", 5, "123").
 * @param maxValue The max value (e.g. - 4).
 * @param offset The offset to add to the passed in number (defaults to 0).
 * @returns The converted number + the offset if the number is smaller than the max (null otherwise).
 */
export const nextNumberOrNull = (value: string | number, maxValue: number, offset = 0.0) => {
  const numVal = (typeof value === 'string' ? parseFloat(value) : value) + offset

  if (numVal >= maxValue) {
    return null
  }

  return numVal
}

export const floorOrNull = (value: unknown) => {
  if (typeof value === 'number') {
    return Math.floor(value)
  }
  return null
}

/**
 *  Formats a phone number string to a more readable format (e.g. - 123-456-7890).
 * @param phoneNumberString The phone number string to format.
 * @returns The formatted phone number string.
 */
export function formatPhoneNumber(phoneNumberString?: string | null) {
  if (!phoneNumberString) {
    return ''
  }

  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return match[1] + '-' + match[2] + '-' + match[3]
  }

  return phoneNumberString
}
